import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ShipmentLabelChip from './ShipmentLabelChip';
import type { ShipmentLabelView } from '../../api/generated/models/ShipmentLabelView';

interface ShipmentLabelsFieldProps {
  labels: ShipmentLabelView[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onManageLabels: () => void;
  disabled?: boolean;
}

const ShipmentLabelsField: React.FC<ShipmentLabelsFieldProps> = ({
  labels,
  selectedIds,
  onChange,
  onManageLabels,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = labels.filter((l) => selectedIds.includes(l.id));

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="shipment-labels-field" ref={ref}>
      <label className="form-label">{t('shipment_field_labels')}</label>
      <button
        type="button"
        className="input shipment-labels-field__trigger"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {selected.length === 0 ? (
          <span className="shipment-labels-field__placeholder">
            {t('shipment_field_labels_placeholder')}
          </span>
        ) : (
          <span className="shipment-labels-field__chips-inline">
            {selected.map((l) => (
              <ShipmentLabelChip key={l.id} label={l} />
            ))}
          </span>
        )}
      </button>
      <p className="form-hint">{t('shipment_field_labels_hint')}</p>
      {open && (
        <div className="shipment-labels-field__dropdown">
          {labels.map((l) => (
            <label key={l.id} className="shipment-labels-field__option">
              <input
                type="checkbox"
                checked={selectedIds.includes(l.id)}
                onChange={() => toggle(l.id)}
              />
              <ShipmentLabelChip label={l} />
            </label>
          ))}
          <button
            type="button"
            className="btn-link shipment-labels-field__manage"
            onClick={() => {
              setOpen(false);
              onManageLabels();
            }}
          >
            {t('shipment_field_labels_manage')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShipmentLabelsField;
